import axios from 'axios';
import { message } from 'antd';

// 网页版API配置
// 开发环境强制走本地相对路径 '/api'，由 webpack-dev-server 代理到线上，避免 CORS
const getApiUrl = () => {
    const isDevEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
    const isBrowser = typeof window !== 'undefined';
    const isLocalHost = isBrowser && /^(localhost|127\.0\.0\.1|0\.0\.0\.0|192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(window.location.hostname);

    if (isDevEnv || isLocalHost) {
        // 提示一次当前基地址，便于排查
        if (typeof console !== 'undefined') {
            // 仅在开发环境打印
            console.info('[API] 使用开发代理基地址: /api');
        }
        return '/api';
    }

    // 生产环境：优先使用注入的环境变量，否则回落到线上地址
    if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    return 'https://note.name666.top/api';
};

const API_URL = getApiUrl();

// 创建axios实例
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    // 移除超时限制，让AI请求有足够时间完成
});

// 网络状态检测 - 修复iOS Safari兼容性问题
let isOnline = navigator.onLine;
let lastNetworkCheck = Date.now();

// iOS Safari的navigator.onLine不可靠，需要额外检测
const isIOSSafari = () => {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
};

// 更可靠的网络状态检测
const checkNetworkStatus = async () => {
    try {
        // 对于iOS Safari，使用实际的网络请求来检测
        if (isIOSSafari()) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);

            await fetch('/favicon.ico', {
                method: 'HEAD',
                signal: controller.signal,
                cache: 'no-cache'
            });
            clearTimeout(timeoutId);
            return true;
        }
        return navigator.onLine;
    } catch {
        return false;
    }
};

window.addEventListener('online', () => {
    isOnline = true;
});
window.addEventListener('offline', () => {
    isOnline = false;
});

// 添加请求拦截器，自动添加token
api.interceptors.request.use(
    async (config) => {
        // 对于iOS Safari，跳过不可靠的网络状态检测
        if (!isIOSSafari()) {
            // 检查网络状态（仅非iOS Safari）
            if (!isOnline) {
                return Promise.reject(new Error('网络连接已断开，请检查网络设置'));
            }
        } else {
            // iOS Safari：每5分钟检查一次网络状态
            const now = Date.now();
            if (now - lastNetworkCheck > 5 * 60 * 1000) {
                isOnline = await checkNetworkStatus();
                lastNetworkCheck = now;
            }
        }

        // 兼容“记住我”与非记住：从 localStorage 或 sessionStorage 读取 token
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 添加响应拦截器，处理错误
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {

        // 网络错误处理
        if (!isOnline) {
            message.error('网络连接已断开，请检查网络设置');
            return Promise.reject(error);
        }

        if (error.code === 'ECONNABORTED') {
            message.error('请求超时，请稍后重试');
            return Promise.reject(error);
        }

        if (error.response) {
            const { status, data } = error.response;
            const errorMessage = data?.message || data?.error || '请求失败';

            switch (status) {
                case 400:
                    // 对于400错误，不在拦截器中显示，让具体组件处理
                    break;
                case 401:
                    // 只有在非登录请求时才自动处理401错误
                    if (!error.config?.url?.includes('/auth/login')) {
                        message.error('登录已过期，请重新登录');
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        // 网页版重新加载页面而不是重定向
                        window.location.reload();
                    } else {
                        message.error(errorMessage || '用户名或密码错误');
                    }
                    break;
                case 403:
                    message.error('权限不足，无法访问该资源');
                    break;
                case 404:
                    message.error('请求的资源不存在');
                    break;
                case 429:
                    message.error('请求过于频繁，请稍后再试');
                    break;
                case 500:
                    message.error('服务器内部错误，请稍后重试');
                    break;
                case 502:
                case 503:
                case 504:
                    message.error('服务暂时不可用，请稍后重试');
                    break;
                default:
                    message.error(errorMessage || `请求失败 (${status})`);
            }
        } else if (error.request) {
            // 请求已发出但没有收到响应
            message.error('网络连接失败，请检查网络设置');
        } else {
            // 其他错误
            message.error('请求配置错误');
        }

        return Promise.reject(error);
    }
);

// 网页版特有的API工具函数
export const apiUtils = {
    // 检查网络状态
    isOnline: () => isOnline,

    // 重试请求
    async retryRequest(requestFn: () => Promise<any>, maxRetries = 3, delay = 1000): Promise<any> {
        for (let i = 0; i < maxRetries; i++) {
            try {
                return await requestFn();
            } catch (error) {
                if (i === maxRetries - 1) throw error;
                await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
            }
        }
    },

    // 批量请求
    async batchRequest(requests: Array<() => Promise<any>>, concurrency = 3): Promise<any[]> {
        const results: any[] = [];
        for (let i = 0; i < requests.length; i += concurrency) {
            const batch = requests.slice(i, i + concurrency);
            const batchResults = await Promise.allSettled(batch.map(req => req()));
            results.push(...batchResults);
        }
        return results;
    },

    // 缓存请求结果
    cache: new Map<string, { data: any; timestamp: number; ttl: number }>(),

    async cachedRequest(key: string, requestFn: () => Promise<any>, ttl = 5 * 60 * 1000): Promise<any> {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < cached.ttl) {
            return cached.data;
        }

        const data = await requestFn();
        this.cache.set(key, { data, timestamp: Date.now(), ttl });
        return data;
    },

    // 清除缓存
    clearCache(pattern?: string) {
        if (pattern) {
            for (const key of this.cache.keys()) {
                if (key.includes(pattern)) {
                    this.cache.delete(key);
                }
            }
        } else {
            this.cache.clear();
        }
    }
};

// 获取标签总数
export const getTagsCount = async () => {
    try {
        const response = await api.get('/tags');
        return response.data.length;
    } catch (error) {
        return 0;
    }
};

// 获取所有标签
export const getAllTags = async () => {
    try {
        const response = await api.get('/tags');
        return response.data;
    } catch (error) {
        return [];
    }
};

export default api;
